import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { getEventTimeZone } from '@/features/events/lib/event.lib';
import {
  type EventInstance,
  toInstanceInput,
} from '@/features/events/lib/eventInstance.lib';
import type { DecoratedEvent } from '@/features/events/types';
import type { PageExperienceFragment } from '@/features/experiences/fragments/PageExperience.generated';
import { getWhenFromValues } from '@/pages/Schedule/components/EntryMessageForm/utils';
import { MINIMUM_ENTRY_MESSAGE_RELATIVE_MINUTES } from '@/pages/Schedule/constants/entry';
import {
  type CreateEventMessageInput,
  DeliveryTimeType,
  HybridRelativeTime,
  type SuggestTextMessageInput,
  TimeDirection,
  TimeUnit,
} from '@/types/gql.generated';
import type { EventMessageFormState, EventModalMessage } from './types';

/**
 * Maximum number of scheduled messages, enforced server-side per instance for a
 * recurring event and per event for a single one.
 */
export const EVENT_SCHEDULED_MESSAGE_LIMIT = 30;

/**
 * Whether the instance a message would be scheduled against has already
 * finished.
 *
 * Measured from the instance's end, not its start, which is the whole point for
 * an all-day event: it starts at midnight, so a start-based check writes off the
 * entire day the moment it begins even though "9am the day of" is still hours
 * away. The same holds for a timed event that is currently underway. A delivery
 * time that really has passed is still refused, by the server
 * (`ENTRY_MESSAGE_IN_PAST`), which judges the send time rather than the event.
 *
 * All-day ends are exclusive (see `createDecoratedMagicEvent`), so a single-day
 * event stays schedulable right up to local midnight. This mirrors
 * `isInstanceInThePast` on the schedule's entry messages panel.
 */
export const isInstanceInThePast = (instance: EventInstance): boolean =>
  instance.end < DateTime.local();

export const createNewEventMessage = (
  event: DecoratedEvent,
  instance: EventInstance
): EventModalMessage => ({
  id: uuidv4(),
  status: null,
  // Provisional — the server assigns the stored instance on create, deriving it
  // from the event itself for single events.
  instance: toInstanceInput(instance.start) ?? '',
  body: '',
  deliveryTimeType: event.isAllDay
    ? DeliveryTimeType.Hybrid
    : DeliveryTimeType.Relative,
  sendToEveryone: true,
  recipients: [],
});

type CreateEventMessageTarget = {
  experienceId: string;
  eventId: string;
  /**
   * ISO start of the instance the message belongs to. Required for recurring
   * events (the server rejects the mutation without it) and ignored server-side
   * for single events, which derive it from the event itself.
   */
  instance?: string | null;
};

/**
 * Shared by the one-at-a-time create (editing an event) and the post-create
 * flush (adding one), so the wire shape can't drift between them — notably
 * `sendToEveryone`, which is derived from the recipient list rather than read
 * off the message.
 */
export const toCreateEventMessageInput = (
  message: EventModalMessage,
  { experienceId, eventId, instance }: CreateEventMessageTarget
): CreateEventMessageInput => ({
  experienceId,
  eventId,
  instance: instance ?? undefined,
  eventMessageId: message.id,
  body: message.body,
  deliveryTimeType: message.deliveryTimeType,
  hybridDeliveryTime: message.hybridDeliveryTime,
  hybridRelativeTime: message.hybridRelativeTime,
  hybridTimeZone: message.hybridTimeZone,
  relativeTimeToEntryStart: message.relativeTimeToEntryStart,
  relativeTimeToEntryStartUnit: message.relativeTimeToEntryStartUnit,
  relativeTimeToEntryStartDirection: message.relativeTimeToEntryStartDirection,
  recipients: message.recipients.map(({ id }) => id),
  sendToEveryone: !message.recipients.length,
});

export const getFormValuesFromEventMessage = (
  message: EventModalMessage,
  event: DecoratedEvent,
  experience: PageExperienceFragment
): EventMessageFormState => {
  const eventTimeZone = getEventTimeZone(event, experience);

  const formValues: EventMessageFormState = {
    when: 'minutes-before',
    body: message.body,
    deliveryTimeType: message.deliveryTimeType,
    sendToEveryone: message.recipients.length > 0 ? false : true,
    hybridDeliveryTime: message.hybridDeliveryTime ?? '09:00',
    hybridRelativeTime: message.hybridRelativeTime ?? HybridRelativeTime.DayOf,
    // An all-day event carries no authored zone, so a "9am the day of" delivery
    // is always anchored to the experience's zone — the server resolves it that
    // way, so honoring a stored UTC value here would only mislabel the field.
    hybridTimeZone: event.isAllDay
      ? eventTimeZone
      : (message.hybridTimeZone ?? eventTimeZone),
    relativeTimeToEntryStart:
      message.relativeTimeToEntryStart ??
      MINIMUM_ENTRY_MESSAGE_RELATIVE_MINUTES,
    relativeTimeToEntryStartUnit:
      message.relativeTimeToEntryStartUnit ?? TimeUnit.Minutes,
    relativeTimeToEntryStartDirection:
      message.relativeTimeToEntryStartDirection ?? TimeDirection.Before,
    recipients: message.recipients.map(({ id }) => id),
  };

  formValues.when = getWhenFromValues(formValues);

  return formValues;
};

// Suggestions are written for the instance the message targets, not the
// series start.
export const toSuggestMessageInput = (
  event: DecoratedEvent,
  instance: EventInstance
): SuggestTextMessageInput => ({
  title: event.title ?? '',
  description: event.description,
  timeZone: event.timeZone,
  location: event.locationWithPlace?.name,
  startDate: instance.start.toUTC().toISO(),
  endDate: instance.end.toUTC().toISO(),
  isOnDay: event.isAllDay,
});
